/**
 * Turns a shape recognized from a freehand stroke (screen coordinates) into
 * a DWG entity (world coordinates).
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type { RecognizedShape } from "../algorithms/shapeRecognition";
import type { Point2D, Point3D } from "./types";
import { deleteObj } from "../core/delete";
import { openAs, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { noteGraphicsModified } from "../telemetry/updateTelemetry";

/** Whether the recognized shape is a closed contour (can carry a hatch) */
export function isClosedShape(shape: RecognizedShape): boolean {
  return shape.kind !== "line";
}

/**
 * Create the DWG entity for a recognized shape and append it to the current
 * space. Lines and polygons become OdDbPolyline (as LineDragger does, to
 * avoid DrawingJS bugs with OdDbLine), circles OdDbCircle, ellipses OdDbEllipse.
 * @param toWorld Screen to world conversion
 * @returns Entity info with object ID, or null on failure
 */
export function buildShapeEntity(
  viewer: DRDrawingViewer,
  shape: RecognizedShape,
  toWorld: (screen: Point2D) => Point3D,
): { id: DrawingModule.OdDbObjectId } | null {
  const module = viewer.module;
  const db = viewer.database;
  if (!db) return null;

  let entity: DrawingModule.OdDbEntity;
  let entityKind: string;

  switch (shape.kind) {
    case "line":
    case "polygon": {
      const vertices =
        shape.kind === "line" ? [shape.start, shape.end] : shape.vertices;
      const polyline = module.OdDbPolyline.createObject();
      polyline.setDatabaseDefaults(db, true);
      vertices.forEach((screen, i) => {
        const world = toWorld(screen);
        const point2d = new module.OdGePoint2d(world.x, world.y);
        polyline.addVertexAt(i, point2d, 0, 0, 0, 0);
        deleteObj(point2d);
      });
      if (shape.kind === "polygon") {
        polyline.setClosed(true);
      }
      entity = polyline;
      entityKind = "Polyline";
      break;
    }

    case "circle": {
      const center = toWorld(shape.center);
      const edge = toWorld({
        x: shape.center.x + shape.radius,
        y: shape.center.y,
      });
      const circle = module.OdDbCircle.createObject();
      circle.setDatabaseDefaults(db, true);
      const centerPoint = new module.OdGePoint3d(center.x, center.y, 0);
      circle.setCenter(centerPoint);
      circle.setRadius(Math.hypot(edge.x - center.x, edge.y - center.y));
      deleteObj(centerPoint);
      entity = circle;
      entityKind = "Circle";
      break;
    }

    case "ellipse": {
      const center = toWorld(shape.center);
      const majorEnd = toWorld({
        x: shape.center.x + Math.cos(shape.rotation) * shape.majorRadius,
        y: shape.center.y + Math.sin(shape.rotation) * shape.majorRadius,
      });
      const ellipse = module.OdDbEllipse.createObject();
      ellipse.setDatabaseDefaults(db, true);
      const centerPoint = new module.OdGePoint3d(center.x, center.y, 0);
      const normal = new module.OdGeVector3d(0, 0, 1);
      const majorAxis = new module.OdGeVector3d(
        majorEnd.x - center.x,
        majorEnd.y - center.y,
        0,
      );
      ellipse.set(
        centerPoint,
        normal,
        majorAxis,
        shape.minorRadius / shape.majorRadius,
        0,
        Math.PI * 2,
      );
      deleteObj(centerPoint);
      deleteObj(normal);
      deleteObj(majorAxis);
      entity = ellipse;
      entityKind = "Ellipse";
      break;
    }
  }

  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );
  const entityId = currentSpace.appendOdDbEntity(entity);
  entity.recordGraphicsModified(true);
  noteGraphicsModified(entityKind);

  deleteObj(currentSpace);
  deleteObj(entity);

  return { id: entityId };
}
